import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Icon,
  CustomColumns,
  CustomTable,
  faTrashArrowUp,
  faFileCircleXmark,
  faFileCircleCheck,
  faTrashCan,
  faEdit,
  faRotateBack,
  TooltipIcon,
} from '@/ui/components';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from '@/translations';
import { AppRoutes } from '@/routes';
import {
  baseSort,
  currencyFormatted,
  sectorConverter,
  toTitleCase,
} from '@/utils/helpers';
import {
  useUpdateInspectionsVehicleDamageAuthorization as useUpdateDamageAuthorization,
  useResolveVehicleDamagePreTimingReview,
  useDeleteInspectionsVehiclesDamagesLines,
  useUnDeleteInspectionsVehiclesDamagesLines,
  useBatchUpdateInspectionsVehicleDamagesStatus,
  inspections,
  useInspectionsVehicleDamageParts,
  useInspectionsVehicleWorkCentres,
  useInspectionsVehiclesDamagesLines,
  useBatchUpdateInspectionsVehiclesDamagesLines,
  usePreTimeSuggestions,
  refreshInspectionIfStatusChanged,
} from '@/api/Inspections';
import {
  ActionsPopup,
  BatchActionsPopup,
} from '@/modules/Inspection/components/tabs/Damage/components/ActionsPopup';
import { DamageLinePopup } from '@/modules/Inspection/components/tabs/Damage/components/DamageLinePopup';
import {
  AppFeatures,
  AuthorizationStatus,
  Damage,
  DamageCondition,
  DataRuleKey,
  RepairMethod,
  Severity,
  Inspection,
  InspectionStatusMap,
  PreTimeSuggestionLine,
} from '@/types';

import { useDeferredInlineEdit } from '@/ui/components/CustomTable/InlineEditApplyContext';
import { App, Button, Drawer, notification, Space, Tag, Tooltip } from 'antd';
import {
  hasClassificationChange,
  inlineDamageToUpdateBody,
  mergeInlineIntoDamageBody,
  type StagedDamageEdit,
} from './stagedDamage';
import { UnsavedDamageChangesPrompt } from './UnsavedDamageChangesPrompt';
import { DamageDetailPanel } from '../DamageDetailPanel';
import { DamageColourLegend } from './DamageColourLegend';
import { getColorForAuthorizationStatus } from './getColorForAuthorizationStatus';
import {
  DamageLineView,
  filterDamageLines,
  isInternallyAmendedDamageLine,
  isPtReviewRequired,
  isRemovedDamageLine,
} from './damageLinePresentation';
import {
  DamageLineReviewBar,
  DamageLineReviewFooter,
  RemovedAppraisalBadge,
} from './DamageLineReviewBar';

const DeferredRowClearCapture: FC<{
  clearRowRef: React.MutableRefObject<(damageLineId: number) => void>;
}> = ({ clearRowRef }) => {
  const deferredEdit = useDeferredInlineEdit<Damage>();
  clearRowRef.current = (damageLineId: number) => {
    deferredEdit?.clearRow(String(damageLineId));
  };
  return null;
};
import {
  PtReviewRequiredModal,
  PtReviewSubmitAction,
} from './PtReviewRequiredModal';
import {
  PreTimeCell,
  PreTimeColumnTitle,
  PreTimeMetrics,
  getPreTimeAggregate,
  getPreTimeCounts,
  getPreTimeStatus,
  preTimeStatusLabelKey,
} from './PreTimeCell';
import { AuditLogTimelineTooltip } from './AuditLogTimelineTooltip';
import { useAppCustomers } from '@/hooks';
import { AddDamageActionsPopup } from '../DamageActions';
import { AddDamagePartsPopup } from '../DamageParts';
import { canEditWorkCentre } from '../canEditWorkCentre';
import { isIstaDamageLine } from '../isIstaDamageLine';
import {
  addFrShortcutHint,
  addPartsShortcutHint,
  editDamageShortcutHint,
} from '../preTimingShortcuts';
import { usePreTimingGridShortcuts } from '../usePreTimingGridShortcuts';
import { PreTimeRecommendationTag } from '../PreTimeSuggestions/PreTimeRecommendationTag';
import { PreTimeSuggestionDetail } from '../PreTimeSuggestions/PreTimeSuggestionDetail';
import { bandLabelKey } from '../PreTimeSuggestions/preTimeSuggestionView';
import { PreTimeAssistantSummary } from '../PreTimeSuggestions/PreTimeAssistantSummary';

export type DamageDataGridDataSource = Damage;

const LOOKUP_CONCURRENCY = 4;

const runLookupQueue = async (
  tasks: Array<() => Promise<unknown>>,
  isActive: () => boolean,
) => {
  let index = 0;
  const worker = async () => {
    while (isActive() && index < tasks.length) {
      const current = index;
      index += 1;
      try {
        await tasks[current]();
      } catch {
        // One failed lookup should not stop the rest of the grid.
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(LOOKUP_CONCURRENCY, tasks.length) }, () =>
      worker(),
    ),
  );
};

const gradeChangeColor = (current: unknown, next: unknown) => {
  const original = Number(current);
  const latest = Number(next);
  if (
    !Number.isFinite(original) ||
    !Number.isFinite(latest) ||
    original === latest
  ) {
    return undefined;
  }
  return latest < original ? '#12b76a' : '#d92d20';
};

interface DamageDataGridProps {
  inspection: Inspection;
  dataSource: DamageDataGridDataSource[];
  canEditDamage?: boolean;
  canEditFr?: boolean;
  canEditParts?: boolean;
  canApproveDamage?: boolean;
  canChangePartComponent?: boolean;
  canChangeDamageType?: boolean;
  canChangeSeverity?: boolean;
  canChangeRepairMethod?: boolean;
  canChangeWorkCenter?: boolean;
  canRemoveDamageLine?: boolean;
  active?: boolean;
  onReload: () => void | Promise<unknown>;
  onGradePreviewChange?: (
    preview: Record<number, { salesGradeValue: number }>,
  ) => void;
}
export const DamageDataGrid: FC<DamageDataGridProps> = ({
  inspection,
  dataSource,
  canEditDamage,
  canEditFr,
  canEditParts,
  canApproveDamage,
  canChangePartComponent,
  canChangeDamageType,
  canChangeSeverity,
  canChangeRepairMethod,
  canChangeWorkCenter,
  canRemoveDamageLine,
  active = true,
  onReload,
  onGradePreviewChange,
}) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const {
    getCustomerDataRule,
    hasFeatureAccess,
    hasDamageApproveAccess,
    hasGbaAccess,
  } = useAppCustomers();

  const customerId = inspection.subCustomerId
    ? inspection.subCustomerId
    : inspection.customerId;

  const isLabour =
    getCustomerDataRule(inspection.customerId, DataRuleKey.DefaultLabour)
      ?.isEnabled || false;

  const activeDamages = dataSource.filter((o) => !o.isDeleted);
  const editableDamages = activeDamages.filter(
    (damage) => !isIstaDamageLine(damage.mechanicalFailure),
  );
  const preTimeAggregate = useMemo(
    () => getPreTimeAggregate(activeDamages),
    [activeDamages],
  );
  const [lineView, setLineView] = useState<DamageLineView>('all');
  const [dismissedNotices, setDismissedNotices] = useState<
    Record<string, boolean>
  >({});
  const [ptReviewDamage, setPtReviewDamage] = useState<Damage>();
  const [ptReviewSubmitting, setPtReviewSubmitting] =
    useState<PtReviewSubmitAction>();

  const [authorization, setAuthorization] = useState<{
    id: number;
    status: AuthorizationStatus;
    backToDefault?: boolean;
  }>();

  const { data: damageParts } = useInspectionsVehicleDamageParts(inspection.id);
  const { mutateAsync: resolvePreTimingReview } =
    useResolveVehicleDamagePreTimingReview();
  const [gradePreview, setGradePreview] = useState<
    Record<number, { valueForGrading: number | null; salesGradeValue: number }>
  >({});
  useEffect(() => {
    onGradePreviewChange?.(gradePreview);
  }, [gradePreview, onGradePreviewChange]);
  const [conditionCodesByPart, setConditionCodesByPart] = useState<
    Record<string, DamageCondition[]>
  >({});
  const [severitiesByKey, setSeveritiesByKey] = useState<
    Record<string, Severity[]>
  >({});
  const [repairsByKey, setRepairsByKey] = useState<
    Record<string, RepairMethod[]>
  >({});
  const conditionCodesRef = useRef(conditionCodesByPart);
  const severitiesRef = useRef(severitiesByKey);
  const repairsRef = useRef(repairsByKey);
  conditionCodesRef.current = conditionCodesByPart;
  severitiesRef.current = severitiesByKey;
  repairsRef.current = repairsByKey;
  const conditionRequests = useRef<Record<string, Promise<DamageCondition[]>>>(
    {},
  );
  const severityRequests = useRef<Record<string, Promise<Severity[]>>>({});
  const repairRequests = useRef<Record<string, Promise<RepairMethod[]>>>({});
  const [lookupLoading, setLookupLoading] = useState(() =>
    dataSource.some((row) => Boolean(row.partCode)),
  );
  const [lookupPending, setLookupPending] = useState<Record<string, number>>(
    {},
  );
  const beginLookup = useCallback((key: string) => {
    setLookupPending((current) => ({
      ...current,
      [key]: (current[key] ?? 0) + 1,
    }));
  }, []);
  const endLookup = useCallback((key: string) => {
    setLookupPending((current) => {
      const count = (current[key] ?? 1) - 1;
      if (count <= 0) {
        const next = { ...current };
        delete next[key];
        return next;
      }
      return { ...current, [key]: count };
    });
  }, []);
  const { data: workCenters } = useInspectionsVehicleWorkCentres(inspection.id);

  const severityKey = (partCode: string, damageTypeCode: string) =>
    `${partCode}|${damageTypeCode}`;
  const repairKey = (
    partCode: string,
    damageTypeCode: string,
    severityId: number,
  ) => `${partCode}|${damageTypeCode}|${severityId}`;

  const loadConditionCodes = useCallback(
    async (partCode: string) => {
      if (!partCode) return [];
      const cached = conditionCodesRef.current[partCode];
      if (cached) return cached;
      const inflight = conditionRequests.current[partCode];
      if (inflight) return inflight;
      const pendingKey = `condition:${partCode}`;
      beginLookup(pendingKey);
      const request = inspections
        .getInspectionsVehicleDamageConditionCodes(inspection.id, partCode)
        .then((response) => response?.data || [])
        .then((data) => {
          setConditionCodesByPart((current) =>
            current[partCode] ? current : { ...current, [partCode]: data },
          );
          return data;
        })
        .finally(() => {
          delete conditionRequests.current[partCode];
          endLookup(pendingKey);
        });
      conditionRequests.current[partCode] = request;
      return request;
    },
    [beginLookup, endLookup, inspection.id],
  );

  const loadSeverities = useCallback(
    async (partCode: string, damageTypeCode: string) => {
      if (!partCode || !damageTypeCode) return [];
      const key = severityKey(partCode, damageTypeCode);
      const cached = severitiesRef.current[key];
      if (cached) return cached;
      const inflight = severityRequests.current[key];
      if (inflight) return inflight;
      const pendingKey = `severity:${key}`;
      beginLookup(pendingKey);
      const request = inspections
        .getInspectionsVehicleSeverityCodes(
          inspection.id,
          partCode,
          damageTypeCode,
        )
        .then((response) => response?.data || [])
        .then((data) => {
          setSeveritiesByKey((current) =>
            current[key] ? current : { ...current, [key]: data },
          );
          return data;
        })
        .finally(() => {
          delete severityRequests.current[key];
          endLookup(pendingKey);
        });
      severityRequests.current[key] = request;
      return request;
    },
    [beginLookup, endLookup, inspection.id],
  );

  const loadRepairs = useCallback(
    async (partCode: string, damageTypeCode: string, severityId: number) => {
      if (!partCode || !damageTypeCode || !severityId) return [];
      const key = repairKey(partCode, damageTypeCode, severityId);
      const cached = repairsRef.current[key];
      if (cached) return cached;
      const inflight = repairRequests.current[key];
      if (inflight) return inflight;
      const pendingKey = `repair:${key}`;
      beginLookup(pendingKey);
      const request = inspections
        .getInspectionsVehicleRepairActionCodes(
          inspection.id,
          partCode,
          damageTypeCode,
          severityId,
        )
        .then((response) => response?.data || [])
        .then((data) => {
          setRepairsByKey((current) =>
            current[key] ? current : { ...current, [key]: data },
          );
          return data;
        })
        .finally(() => {
          delete repairRequests.current[key];
          endLookup(pendingKey);
        });
      repairRequests.current[key] = request;
      return request;
    },
    [beginLookup, endLookup, inspection.id],
  );

  useEffect(() => {
    const tasks: Array<() => Promise<unknown>> = [];
    const queued = new Set<string>();
    const enqueue = (key: string, task: () => Promise<unknown>) => {
      if (queued.has(key)) return;
      queued.add(key);
      tasks.push(task);
    };
    dataSource.forEach((row) => {
      if (row.isDeleted || !row.partCode) return;
      if (!conditionCodesRef.current[row.partCode]) {
        enqueue(`condition:${row.partCode}`, () =>
          loadConditionCodes(row.partCode),
        );
      }
      if (!row.damageConditionCode) return;
      const severityCacheKey = severityKey(
        row.partCode,
        row.damageConditionCode,
      );
      if (!severitiesRef.current[severityCacheKey]) {
        enqueue(`severity:${severityCacheKey}`, () =>
          loadSeverities(row.partCode, row.damageConditionCode),
        );
      }
      if (!row.severityId) return;
      const repairCacheKey = repairKey(
        row.partCode,
        row.damageConditionCode,
        row.severityId,
      );
      if (!repairsRef.current[repairCacheKey]) {
        enqueue(`repair:${repairCacheKey}`, () =>
          loadRepairs(row.partCode, row.damageConditionCode, row.severityId),
        );
      }
    });

    if (tasks.length === 0) {
      setLookupLoading(false);
      return;
    }

    let active = true;
    setLookupLoading(true);
    void runLookupQueue(tasks, () => active).then(() => {
      if (active) setLookupLoading(false);
    });
    return () => {
      active = false;
    };
  }, [dataSource, loadConditionCodes, loadRepairs, loadSeverities]);

  const {
    mutateAsync: updateDamageAuthorizationAsync,
    isPending: updateAuthorizationLoading,
  } = useUpdateDamageAuthorization();
  const {
    mutateAsync: batchUpdateDamageLinesAsync,
    isPending: batchUpdateDamageLinesLoading,
  } = useBatchUpdateInspectionsVehiclesDamagesLines();
  const [stagedEdits, setStagedEdits] = useState<
    Record<number, StagedDamageEdit>
  >({});
  const stagedEditsRef = useRef(stagedEdits);
  stagedEditsRef.current = stagedEdits;
  const [inlineChanges, setInlineChanges] = useState<
    Record<number, Partial<Damage>>
  >({});
  const {
    mutateAsync: batchUpdateVehicleDamageStatusMutateAsync,
    isPending: batchUpdateLoading,
  } = useBatchUpdateInspectionsVehicleDamagesStatus();
  const {
    mutateAsync: deleteVehicleDamageLineMutateAsync,
    isPending: deleteLoading,
  } = useDeleteInspectionsVehiclesDamagesLines();
  const {
    mutateAsync: unDeleteVehicleDamageLineMutateAsync,
    isPending: unDeleteLoading,
  } = useUnDeleteInspectionsVehiclesDamagesLines();

  const { isFetching: damageLinesLoading } = useInspectionsVehiclesDamagesLines(
    inspection.id,
  );
  const reloadData = useCallback(() => onReload(), [onReload]);

  const handleAuthorizationSubmit = useCallback(
    async (comment?: string) => {
      if (!authorization) {
        throw new Error('Authorization is missing.');
      }

      const res = await updateDamageAuthorizationAsync({
        id: authorization.id,
        authorizationNotes: comment,
        authorizationStatus: authorization.backToDefault
          ? AuthorizationStatus.Default
          : authorization.status,
      });

      if (res.success) {
        reloadData();
        refreshInspectionIfStatusChanged(inspection.id, res.data);
        return;
      }

      throw new Error(res.message?.Message || 'Something went wrong...');
    },
    [authorization, inspection.id, reloadData, updateDamageAuthorizationAsync],
  );

  const handleDeferredApplyComplete = useCallback(
    async (updates: Array<{ record: Damage; changes: Partial<Damage> }>) => {
      try {
        const stagedSnapshot = stagedEditsRef.current;
        const stagedIds = new Set(
          Object.keys(stagedSnapshot).map((id) => Number(id)),
        );
        const modalBodies = Object.values(stagedSnapshot).map((edit) => {
          const inline = updates.find(
            (item) => item.record.id === edit.body.damageLineId,
          );
          return mergeInlineIntoDamageBody(edit.body, inline?.changes);
        });
        const inlineOnly = updates.filter(
          (item) => !stagedIds.has(item.record.id),
        );
        const clearAppliedState = () => {
          setStagedEdits({});
          setInlineChanges({});
          setGradePreview((current) => {
            const next = { ...current };
            Object.keys(stagedSnapshot).forEach((id) => {
              delete next[Number(id)];
            });
            updates.forEach((update) => {
              delete next[update.record.id];
            });
            return next;
          });
        };

        const inlineBodies = (
          await Promise.all(
            inlineOnly.map(async ({ record: oldData, changes: newData }) => {
              if (isIstaDamageLine(oldData.mechanicalFailure)) return null;

              const partChanged =
                newData.partCode !== undefined &&
                newData.partCode !== oldData.partCode;
              const damageTypeChanged =
                newData.damageConditionCode !== undefined &&
                newData.damageConditionCode !== oldData.damageConditionCode;
              const severityChanged =
                newData.severityId !== undefined &&
                newData.severityId !== oldData.severityId;
              const repairChanged =
                newData.repairActionCode !== undefined &&
                newData.repairActionCode !== oldData.repairActionCode;
              const workCenterChanged =
                newData.workCenterCode !== undefined &&
                newData.workCenterCode !== oldData.workCenterCode;

              const hasValue = (value: unknown) =>
                value != null && String(value).length > 0;

              if (
                partChanged &&
                (!hasValue(newData.damageConditionCode) ||
                  !hasValue(newData.severityId) ||
                  !hasValue(newData.repairActionCode))
              ) {
                throw new Error(
                  'Please re-select damage type, severity and repair method after changing part.',
                );
              }

              if (
                damageTypeChanged &&
                (!hasValue(newData.severityId) ||
                  !hasValue(newData.repairActionCode))
              ) {
                throw new Error(
                  'Please re-select severity and repair method after changing damage type.',
                );
              }

              if (severityChanged && !hasValue(newData.repairActionCode)) {
                throw new Error(
                  'Please re-select repair method after changing severity.',
                );
              }

              if (
                !partChanged &&
                !damageTypeChanged &&
                !severityChanged &&
                !repairChanged &&
                !workCenterChanged
              ) {
                return null;
              }

              const merged = { ...oldData, ...newData };
              const classificationChanged =
                partChanged || damageTypeChanged || repairChanged;
              const repairs =
                classificationChanged &&
                merged.partCode &&
                merged.damageConditionCode &&
                merged.severityId
                  ? await loadRepairs(
                      merged.partCode,
                      merged.damageConditionCode,
                      Number(merged.severityId),
                    )
                  : [];
              const repairActionId = repairs.find(
                (item) => item.code === newData.repairActionCode,
              )?.id;
              if (classificationChanged && !repairActionId) {
                throw new Error(
                  'Repair method is not valid for the selected part, damage type and severity.',
                );
              }

              return inlineDamageToUpdateBody(
                oldData,
                inspection.id,
                inspection.locationId,
                newData,
              );
            }),
          )
        ).filter((item): item is NonNullable<typeof item> => item != null);

        const items = [...modalBodies, ...inlineBodies];
        if (items.length === 0) {
          setInlineChanges((current) => {
            const next = { ...current };
            updates.forEach((update) => {
              delete next[update.record.id];
            });
            return next;
          });
          return;
        }

        const res = await batchUpdateDamageLinesAsync({
          inspectionId: inspection.id,
          items,
        });

        if (res.data?.inspectionStatusChanged && res.data.canView === false) {
          clearAppliedState();
          notification.success({
            message: t('inspectionStatusChanged'),
            description: t('backToInspectionsSoon'),
            duration: 3,
            onClose: () => navigate(AppRoutes.inspections),
          });
          return;
        }

        if (res.data?.inspectionStatusChanged) {
          refreshInspectionIfStatusChanged(inspection.id, true);
        }

        clearAppliedState();
        await reloadData();
        const removedPreTiming = (res.data?.changes || []).some(
          (change) =>
            change.preTimingRemovedActions?.length ||
            change.preTimingRemovedParts?.length,
        );
        message.success(
          removedPreTiming
            ? t('classificationClearedPreTiming')
            : inlineBodies.length > 0
              ? t('damageClassificationUpdated')
              : t('damageChangesApplied'),
        );
      } catch (error) {
        const serverMessage = (error as any).response?.data?.entities?.[0]
          ?.message;

        message.error(serverMessage || (error as Error).message);
        throw error;
      }
    },
    [
      inspection.id,
      inspection.locationId,
      loadRepairs,
      message,
      navigate,
      reloadData,
      t,
      batchUpdateDamageLinesAsync,
    ],
  );

  const handleStageDamage = useCallback(
    (edit: StagedDamageEdit) => {
      const id = edit.body.damageLineId;
      setStagedEdits((current) => ({ ...current, [id]: edit }));
      setInlineChanges((current) => {
        if (!(id in current)) return current;
        const next = { ...current };
        delete next[id];
        return next;
      });

      const original = dataSource.find((row) => row.id === id);
      if (
        original &&
        edit.salesGradeValue != null &&
        edit.salesGradeValue !== original.salesGradeValue
      ) {
        setGradePreview((current) => ({
          ...current,
          [id]: {
            valueForGrading: edit.valueForGrading ?? null,
            salesGradeValue: edit.salesGradeValue as number,
          },
        }));
      } else if (edit.salesGradeValue != null) {
        setGradePreview((current) => {
          if (!(id in current)) return current;
          const next = { ...current };
          delete next[id];
          return next;
        });
      }

      const partCode = String(
        edit.display.partCode ?? original?.partCode ?? '',
      );
      const damageTypeCode = String(
        edit.display.damageConditionCode ?? original?.damageConditionCode ?? '',
      );
      const severityId = Number(
        edit.display.severityId ?? original?.severityId ?? 0,
      );
      if (partCode) void loadConditionCodes(partCode);
      if (partCode && damageTypeCode) {
        void loadSeverities(partCode, damageTypeCode);
      }
      if (partCode && damageTypeCode && severityId) {
        void loadRepairs(partCode, damageTypeCode, severityId);
      }
    },
    [dataSource, loadConditionCodes, loadRepairs, loadSeverities],
  );

  const clearDeferredRowRef = useRef<(damageLineId: number) => void>(
    () => undefined,
  );
  const restoreDamageLine = useCallback((damageLineId: number) => {
    clearDeferredRowRef.current(damageLineId);
    setStagedEdits((current) => {
      if (!(damageLineId in current)) return current;
      const next = { ...current };
      delete next[damageLineId];
      return next;
    });
    setInlineChanges((current) => {
      if (!(damageLineId in current)) return current;
      const next = { ...current };
      delete next[damageLineId];
      return next;
    });
    setGradePreview((current) => {
      if (!(damageLineId in current)) return current;
      const next = { ...current };
      delete next[damageLineId];
      return next;
    });
  }, []);

  const handleDeleteDamageLine = useCallback(
    async (damageLineId: number) => {
      try {
        modal.confirm({
          title: 'Confirm',
          content: t('deleteRecordQuestion'),
          okText: t('yes'),
          onOk: async () => {
            const res = await deleteVehicleDamageLineMutateAsync({
              damageLineId,
              inspectionId: inspection.id,
            });
            if (!res.success) {
              message.error(res.message.Message);
            } else {
              reloadData();
            }
          },
          onCancel: () => {},
        });
      } catch (error) {
        console.log('handleDeleteDamageLine: ', { error });
      }
    },
    [
      deleteVehicleDamageLineMutateAsync,
      inspection.id,
      message,
      modal,
      reloadData,
      t,
    ],
  );

  const submitPreTimingReview = useCallback(
    async (damage: Damage, action: PtReviewSubmitAction) => {
      if (!hasGbaAccess) {
        message.error(t('ptReviewInternalOnly'));
        return;
      }
      setPtReviewSubmitting(action);
      try {
        await resolvePreTimingReview({
          inspectionId: inspection.id,
          damageLineId: damage.id,
          keepPreTiming: action === 'keep',
        });
        setPtReviewDamage(undefined);
        await reloadData();
      } catch (error) {
        message.error((error as Error).message);
      } finally {
        setPtReviewSubmitting(undefined);
      }
    },
    [
      hasGbaAccess,
      inspection.id,
      message,
      reloadData,
      resolvePreTimingReview,
      t,
    ],
  );

  const handleUnDeleteDamageLine = useCallback(
    async (damageLineId: number) => {
      try {
        await unDeleteVehicleDamageLineMutateAsync({
          damageLineId,
          inspectionId: inspection.id,
        });
        reloadData();
      } catch (error) {
        console.log('handleUnDeleteDamageLine: ', { error });
      }
    },
    [inspection.id, reloadData, unDeleteVehicleDamageLineMutateAsync],
  );

  const handleBatchChangeStatus = useCallback(
    async (status: AuthorizationStatus, comment: string) => {
      const res = await batchUpdateVehicleDamageStatusMutateAsync({
        inspectionId: inspection.id,
        authorizationStatus: status,
        authorizationNotes: comment,
      });
      reloadData();
      refreshInspectionIfStatusChanged(inspection.id, res.data);
    },
    [batchUpdateVehicleDamageStatusMutateAsync, inspection.id, reloadData],
  );

  const [createDamageLine, setCreateDamageLine] = useState(false);
  const [editDamageLine, setEditDamageLine] =
    useState<DamageDataGridDataSource>();
  const [editOriginalDamage, setEditOriginalDamage] =
    useState<DamageDataGridDataSource>();

  const [batchAuthorizationStatus, setBatchAuthorizationStatus] =
    useState<AuthorizationStatus>();

  const [addActions, setAddActions] = useState(false);
  const [addParts, setAddParts] = useState(false);
  const [detailDamageId, setDetailDamageId] = useState<number>();
  const [showPreTimeDetail, setShowPreTimeDetail] = useState(false);
  const [selectedDamageId, setSelectedDamageId] = useState<number>();
  const addFrTriggerRef = useRef<HTMLButtonElement>(null);
  const editDamageTriggerRef = useRef<HTMLElement | null>(null);

  const detailDamage = dataSource.find((item) => item.id === detailDamageId);

  const canEditDamageDetail = useCallback(
    (data: Damage, preCheck?: boolean) =>
      !!preCheck &&
      !data.authorizationStatusNum &&
      !data.isDeleted &&
      !isIstaDamageLine(data.mechanicalFailure),
    [],
  );

  const visibleDamageLines = useMemo(
    () => filterDamageLines(dataSource, lineView),
    [dataSource, lineView],
  );
  const stagedRowIds = useMemo(() => {
    const ids = new Set<number>(
      Object.keys(stagedEdits).map((id) => Number(id)),
    );
    Object.entries(inlineChanges).forEach(([id, changes]) => {
      if (Object.keys(changes).length > 0) ids.add(Number(id));
    });
    return ids;
  }, [inlineChanges, stagedEdits]);
  const tableDamageLines = useMemo(
    () =>
      visibleDamageLines.map((row) => {
        const display = stagedEdits[row.id]?.display;
        return display ? { ...row, ...display } : row;
      }),
    [stagedEdits, visibleDamageLines],
  );
  const visibleDamageIds = useMemo(
    () => visibleDamageLines.map((damage) => damage.id),
    [visibleDamageLines],
  );
  const defaultDisplayedDamageIds = useMemo(
    () =>
      [...visibleDamageLines]
        .sort((left, right) => baseSort(left, right, 'partCode'))
        .map((damage) => damage.id),
    [visibleDamageLines],
  );
  const getDisplayedDamageIds = useCallback(() => {
    const rows = document.querySelectorAll(
      '.separateRowsTable .ant-table-tbody > tr.ant-table-row[data-row-key]',
    );
    const ids: number[] = [];
    const seen = new Set<number>();
    rows.forEach((row) => {
      const id = Number(row.getAttribute('data-row-key'));
      if (!Number.isFinite(id) || seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    });
    return ids.length > 0 ? ids : visibleDamageIds;
  }, [visibleDamageIds]);
  const selectDamageLine = useCallback((id: number) => {
    setSelectedDamageId(id);
    requestAnimationFrame(() => {
      document
        .querySelector(
          `.separateRowsTable .ant-table-body tr[data-row-key="${id}"]`,
        )
        ?.scrollIntoView?.({ block: 'nearest' });
    });
  }, []);

  useEffect(() => {
    if (
      selectedDamageId != null &&
      visibleDamageIds.includes(selectedDamageId)
    ) {
      return;
    }
    setSelectedDamageId(defaultDisplayedDamageIds[0]);
  }, [defaultDisplayedDamageIds, selectedDamageId, visibleDamageIds]);

  const selectedDamage = visibleDamageLines.find(
    (damage) => damage.id === selectedDamageId,
  );
  const canEditSelectedDamage =
    !!selectedDamage &&
    canEditDamageDetail(
      selectedDamage,
      canEditDamage ||
        canChangePartComponent ||
        canChangeDamageType ||
        canChangeSeverity ||
        canChangeRepairMethod ||
        canChangeWorkCenter,
    );
  const canAddFrToSelected =
    !!canEditFr &&
    !!selectedDamage &&
    canEditDamageDetail(selectedDamage, canEditFr);

  const popupOpen =
    addActions ||
    addParts ||
    createDamageLine ||
    !!editDamageLine ||
    !!authorization ||
    !!ptReviewDamage ||
    !!detailDamage ||
    batchAuthorizationStatus != null;

  const saving =
    batchUpdateDamageLinesLoading ||
    updateAuthorizationLoading ||
    batchUpdateLoading ||
    deleteLoading ||
    unDeleteLoading ||
    !!ptReviewSubmitting;

  const canAddParts = !!canEditParts && editableDamages.length > 0;

  const openAddParts = useCallback(() => {
    if (!canAddParts) return;
    setAddParts(true);
  }, [canAddParts]);

  const openAddFrForSelected = useCallback(() => {
    if (!canAddFrToSelected) return;
    setAddActions(true);
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-row-key="${selectedDamageId}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    });
  }, [canAddFrToSelected, selectedDamageId]);

  const openDamageEditor = useCallback(
    (damageLineId: number) => {
      const original = dataSource.find((row) => row.id === damageLineId);
      if (
        !original ||
        !canEditDamageDetail(
          original,
          canEditDamage ||
            canChangePartComponent ||
            canChangeDamageType ||
            canChangeSeverity ||
            canChangeRepairMethod ||
            canChangeWorkCenter,
        )
      ) {
        return;
      }
      setEditOriginalDamage(original);
      setEditDamageLine({
        ...original,
        ...stagedEdits[original.id]?.display,
        ...inlineChanges[original.id],
      });
      setSelectedDamageId(original.id);
    },
    [
      canChangeDamageType,
      canChangePartComponent,
      canChangeRepairMethod,
      canChangeSeverity,
      canChangeWorkCenter,
      canEditDamage,
      canEditDamageDetail,
      dataSource,
      inlineChanges,
      stagedEdits,
    ],
  );

  const openEditForSelected = useCallback(() => {
    if (selectedDamageId == null) return;
    openDamageEditor(selectedDamageId);
  }, [openDamageEditor, selectedDamageId]);

  usePreTimingGridShortcuts({
    enabled: active,
    saving,
    popupOpen,
    canAddFr: canAddFrToSelected,
    canAddParts,
    canEditDamage: canEditSelectedDamage,
    selectedId: selectedDamageId,
    visibleIds: visibleDamageIds,
    getVisibleIds: getDisplayedDamageIds,
    onSelect: selectDamageLine,
    onAddFr: openAddFrForSelected,
    onAddParts: openAddParts,
    onEditDamage: openEditForSelected,
  });

  const canEditWorkCentreInline = useCallback(
    (data: Damage) =>
      canEditWorkCentre({
        canChangeWorkCenter,
        isDeleted: data.isDeleted,
        mechanicalFailure: data.mechanicalFailure,
      }),
    [canChangeWorkCenter],
  );

  const canShowPreTimeSuggestions = !!canEditFr && !!canEditParts;
  const { data: preTimeSuggestions = [] } = usePreTimeSuggestions(
    inspection.id,
    canShowPreTimeSuggestions,
  );
  const [suggestionDamageId, setSuggestionDamageId] = useState<number>();
  const istaDamageLineIds = useMemo(
    () =>
      new Set(
        dataSource
          .filter((damage) => isIstaDamageLine(damage.mechanicalFailure))
          .map((damage) => damage.id),
      ),
    [dataSource],
  );
  const suggestionById = useMemo(() => {
    const map = new Map<number, PreTimeSuggestionLine>();
    preTimeSuggestions.forEach((line) => {
      if (istaDamageLineIds.has(line.damageLineId)) return;
      map.set(line.damageLineId, line);
    });
    return map;
  }, [istaDamageLineIds, preTimeSuggestions]);
  const suggestionLine =
    suggestionDamageId == null
      ? undefined
      : suggestionById.get(suggestionDamageId);

  const columnFeatures: { dataIndex: keyof Damage; feature: AppFeatures }[] = [
    { dataIndex: 'damageAreaDesc', feature: AppFeatures.DamageGridLocation },
    { dataIndex: 'partCode', feature: AppFeatures.DamageGridPartComp },
    {
      dataIndex: 'damageConditionCode',
      feature: AppFeatures.DamageGridDamageType,
    },
    { dataIndex: 'sector', feature: AppFeatures.DamageGridSector },
    { dataIndex: 'severityId', feature: AppFeatures.DamageGridSeverity },
    {
      dataIndex: 'repairActionCode',
      feature: AppFeatures.DamageGridRepairMethod,
    },
    {
      dataIndex: 'internalGradingPoints',
      feature: AppFeatures.DamageGridInternalGrade,
    },
    {
      dataIndex: 'workCenterCode',
      feature: AppFeatures.DamageGridWorkCentre,
    },
    {
      dataIndex: 'responsibilityDesc',
      feature: AppFeatures.DamageGridResponsibility,
    },
    {
      dataIndex: 'valueForGrading',
      feature: AppFeatures.DamageGridGradePoints,
    },
    { dataIndex: 'lineCost', feature: AppFeatures.DamageGridLineCost },
    { dataIndex: 'note', feature: AppFeatures.DamageGridNote },
    { dataIndex: 'auditLogs', feature: AppFeatures.DamageGridTimeline },
    { dataIndex: 'id', feature: AppFeatures.DamageGridActions },
  ];

  const classificationChanged = useCallback(
    (data: Damage) => {
      const saved = dataSource.find((row) => row.id === data.id) ?? data;
      const inline = inlineChanges[data.id];
      const partCode =
        inline && 'partCode' in inline ? inline.partCode : data.partCode;
      const damageConditionCode =
        inline && 'damageConditionCode' in inline
          ? inline.damageConditionCode
          : data.damageConditionCode;
      return hasClassificationChange(saved, { partCode, damageConditionCode });
    },
    [dataSource, inlineChanges],
  );

  const severityChanged = useCallback(
    (data: Damage) => {
      const saved = dataSource.find((row) => row.id === data.id) ?? data;
      const inline = inlineChanges[data.id];
      const currentSeverity =
        inline && 'severityId' in inline ? inline.severityId : data.severityId;
      return String(currentSeverity ?? '') !== String(saved.severityId ?? '');
    },
    [dataSource, inlineChanges],
  );

  const canEditSeverity = useCallback(
    (data: Damage) =>
      canEditDamageDetail(
        data,
        !!canChangeSeverity || classificationChanged(data),
      ),
    [canChangeSeverity, canEditDamageDetail, classificationChanged],
  );

  const canEditRepair = useCallback(
    (data: Damage) =>
      canEditDamageDetail(
        data,
        !!canChangeRepairMethod ||
          classificationChanged(data) ||
          severityChanged(data),
      ),
    [
      canChangeRepairMethod,
      canEditDamageDetail,
      classificationChanged,
      severityChanged,
    ],
  );

  const columns: CustomColumns<Damage> = useMemo(
    () => [
      {
        dataIndex: 'damageAreaDesc',
        title: t('location'),
        width: 180,
        fixed: 'left',
        sortable: true,
        filter: true,
        render: (value) => toTitleCase(value),
      },
      {
        dataIndex: 'partCode',
        title: t('partComp'),
        width: 180,
        sortable: true,
        filter: true,
        defaultSortOrder: 'ascend',
        render: (_, record) => toTitleCase(record.damagePartDesc),
        editable: (data) => canEditDamageDetail(data, canChangePartComponent),
        renderType: 'select',
        clearFieldsOnEdit: [
          'damageConditionCode',
          'severityId',
          'repairActionCode',
        ],
        renderOptions: (data: Damage) =>
          damageParts?.selectItems?.[
            data.areaCode as keyof typeof damageParts.selectItems
          ] || [],
      },
      {
        dataIndex: 'damageConditionCode',
        title: t('damageType'),
        width: 180,
        sortable: true,
        filter: true,
        render: (_, record) => toTitleCase(record.damageTypeDesc),
        editable: (data) => canEditDamageDetail(data, canChangeDamageType),
        renderType: 'select',
        clearFieldsOnEdit: ['severityId', 'repairActionCode'],
        renderOptions: (data: Damage) =>
          (conditionCodesByPart[data.partCode] || []).map((item) => ({
            label: item.description,
            value: item.code,
          })),
        optionsLoading: (data: Damage) =>
          Boolean(data.partCode && lookupPending[`condition:${data.partCode}`]),
      },
      {
        dataIndex: 'severityId',
        title: t('severity'),
        width: 160,
        // add this for not editable rows
        render: (_, record) => record.severityDesc,
        editable: (data) => canEditSeverity(data),
        renderType: 'select',
        clearFieldsOnEdit: ['repairActionCode'],
        renderOptions: (data: Damage) =>
          !data.partCode || !data.damageConditionCode
            ? []
            : (
                severitiesByKey[
                  severityKey(data.partCode, data.damageConditionCode)
                ] || []
              ).map((item) => ({
                label: item.description,
                value: item.severityId,
              })),
        optionsLoading: (data: Damage) =>
          Boolean(
            data.partCode &&
              data.damageConditionCode &&
              lookupPending[
                `severity:${severityKey(data.partCode, data.damageConditionCode)}`
              ],
          ),
        filterOptions: Array.from(
          dataSource.reduce((options, row) => {
            if (row.severityId && !options.has(row.severityId)) {
              options.set(row.severityId, row.severityDesc || '');
            }
            return options;
          }, new Map<number, string>()),
        ).map(([id, text]) => ({
          value: id,
          text,
        })),
        filter: true,
        sortable: true,
      },
      {
        dataIndex: 'repairActionCode',
        title: t('repairMethod'),
        width: 180,
        render: (_, record) => record.repairActionDesc,
        editable: (data) => canEditRepair(data),
        renderType: 'select',
        renderOptions: (data: Damage) =>
          !data.partCode || !data.damageConditionCode || !data.severityId
            ? []
            : (
                repairsByKey[
                  repairKey(
                    data.partCode,
                    data.damageConditionCode,
                    data.severityId,
                  )
                ] || []
              ).map((item) => ({
                label: item.description,
                value: item.code,
              })),
        optionsLoading: (data: Damage) =>
          Boolean(
            data.partCode &&
              data.damageConditionCode &&
              data.severityId &&
              lookupPending[
                `repair:${repairKey(
                  data.partCode,
                  data.damageConditionCode,
                  data.severityId,
                )}`
              ],
          ),
        filter: true,
        sortable: true,
      },
      {
        dataIndex: 'internalGradingPoints',
        title: t('internalGradePoints'),
        width: 140,
        filter: true,
        sortable: true,
        render: (_, record) => {
          const preview = gradePreview[record.id];
          const changeColor = preview
            ? gradeChangeColor(record.salesGradeValue, preview.salesGradeValue)
            : undefined;
          return (
            <span>
              {`${record.internalGradingPoints} (${record.salesGradeValue}`}
              {preview ? (
                <span
                  style={{
                    color: changeColor,
                    fontWeight: changeColor ? 600 : undefined,
                  }}
                >
                  {` -> ${preview.salesGradeValue}`}
                </span>
              ) : null}
              )
            </span>
          );
        },
        onFilter: (value, record) => {
          const preview = gradePreview[record.id];
          const text = `${record.internalGradingPoints} (${record.salesGradeValue}${preview ? ` -> ${preview.salesGradeValue}` : ''})`;
          return text.toLowerCase().includes(String(value ?? '').toLowerCase());
        },
      },
      {
        dataIndex: 'sector',
        title: t('sector'),
        width: 80,
        render: (value) => sectorConverter(value),
        sortable: true,
        filter: true,
      },
      {
        dataIndex: 'workCenterCode',
        title: t('workCentre'),
        width: 160,
        render: (_, record) => record.workCenterDesc,
        editable: (data) => canEditWorkCentreInline(data),
        renderType: 'select',
        renderOptions: () => workCenters || [],
        filter: true,
        sortable: true,
      },
      {
        dataIndex: 'responsibilityDesc',
        title: t('responsibility'),
        width: 250,
        filter: true,
        sortable: true,
      },
      {
        dataIndex: 'lineCost',
        title: t('lineCost'),
        render: (_, record) =>
          currencyFormatted(
            hasDamageApproveAccess ? record.approvalCost : record.lineCost,
          ),
        width: 100,
        filter: true,
        sortable: true,
        align: 'center',
      },
      {
        dataIndex: 'note',
        title: t('note'),
        width: 120,
        filter: true,
        ellipsis: true,
      },
      {
        dataIndex: 'auditLogs',
        onCell: () => ({ className: 'row-actions' }),
        render: (auditLogs) => (
          <AuditLogTimelineTooltip auditLogs={auditLogs} />
        ),
        align: 'center',
        width: 40,
        exportable: false,
      },
      {
        dataIndex: 'id',
        title: (
          <PreTimeColumnTitle
            expanded={showPreTimeDetail}
            onToggle={() => setShowPreTimeDetail((current) => !current)}
          />
        ),
        titleString: t('preTime'),
        width: showPreTimeDetail ? 340 : 88,
        fixed: 'right',
        render: (_, record: Damage) => (
          <PreTimeCell
            damage={record}
            expanded={showPreTimeDetail}
            open={detailDamageId === record.id}
            reviewRequired={isPtReviewRequired(record)}
            amended={isInternallyAmendedDamageLine(record)}
            onReview={
              hasGbaAccess && isPtReviewRequired(record)
                ? () => setPtReviewDamage(record)
                : undefined
            }
            onOpenDetail={() =>
              setDetailDamageId((current) =>
                current === record.id ? undefined : record.id,
              )
            }
          />
        ),
        exportRender: (_, record) => {
          const status = getPreTimeStatus(record);
          const { frCount, partsCount, materialsCount } =
            getPreTimeCounts(record);
          return `${t(preTimeStatusLabelKey(status))} ${t('fr')} ${frCount} ${t('parts')} ${partsCount} ${t('materials')} ${materialsCount}`;
        },
      },
      ...(canShowPreTimeSuggestions
        ? ([
            {
              dataIndex: 'id',
              title: t('recommendation'),
              titleString: t('recommendation'),
              width: 160,
              align: 'center' as const,
              fixed: 'right' as const,
              exportRender: (_: unknown, record: Damage) => {
                const line = suggestionById.get(record.id);
                return line ? t(bandLabelKey[line.band]) : '';
              },
              render: (_: unknown, record: Damage) => {
                const line = suggestionById.get(record.id);
                if (!line) return null;
                return (
                  <PreTimeRecommendationTag
                    band={line.band}
                    label={t(bandLabelKey[line.band])}
                    onClick={() => setSuggestionDamageId(record.id)}
                  />
                );
              },
            },
          ] as CustomColumns<Damage>)
        : []),
      {
        dataIndex: 'id',
        // 1. Show un-delete if line deleted
        // 2. Show un-approve/un-reject if approved/rejected
        // 3. Show approve/reject and edit/delete
        onCell: () => ({ className: 'row-actions' }),
        render: (_, data) => (
          <Space>
            {data.isDeleted ? (
              <RemovedAppraisalBadge>
                {t('removedFromAppraisal')}
              </RemovedAppraisalBadge>
            ) : null}
            {stagedRowIds.has(data.id) ? (
              <Tag
                color="blue"
                data-testid={`damage-staged-${data.id}`}
                style={{
                  margin: 0,
                  borderRadius: 999,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {t('staged')}
                <button
                  type="button"
                  aria-label={t('restoreOriginal')}
                  data-testid={`damage-restore-${data.id}`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    restoreDamageLine(data.id);
                  }}
                  style={{
                    border: 0,
                    padding: 0,
                    background: 'transparent',
                    color: 'inherit',
                    cursor: 'pointer',
                    lineHeight: 1,
                  }}
                >
                  ↺
                </button>
              </Tag>
            ) : null}
            {data.isDeleted && canRemoveDamageLine ? (
              <TooltipIcon
                color="#FD7E14"
                icon={faTrashArrowUp}
                title={t('unDelete')}
                onClick={() => handleUnDeleteDamageLine(data.id)}
              />
            ) : data.authorizationStatusNum ? (
              canApproveDamage ? (
                <TooltipIcon
                  color="#FD7E14"
                  icon={faRotateBack}
                  title={t(
                    data.authorizationStatusNum === AuthorizationStatus.Approved
                      ? 'unApprove'
                      : 'unReject',
                  )}
                  onClick={() =>
                    setAuthorization({
                      id: data.id,
                      status: data.authorizationStatusNum,
                      backToDefault: true,
                    })
                  }
                />
              ) : null
            ) : (
              <>
                {canApproveDamage ? (
                  <>
                    <TooltipIcon
                      color="#2b8a3e"
                      icon={faFileCircleCheck}
                      title={t('approve')}
                      onClick={() =>
                        setAuthorization({
                          id: data.id,
                          status: AuthorizationStatus.Approved,
                        })
                      }
                    />
                    <TooltipIcon
                      color="#d9480f"
                      icon={faFileCircleXmark}
                      title={t('reject')}
                      onClick={() =>
                        setAuthorization({
                          id: data.id,
                          status: AuthorizationStatus.Rejected,
                        })
                      }
                    />
                  </>
                ) : null}

                {canEditDamageDetail(
                  data,
                  canEditDamage ||
                    canChangePartComponent ||
                    canChangeDamageType ||
                    canChangeSeverity ||
                    canChangeRepairMethod ||
                    canChangeWorkCenter,
                ) ? (
                  <TooltipIcon
                    color="#339AF0"
                    icon={faEdit}
                    title={`${t('edit')} (${editDamageShortcutHint()})`}
                    onClick={(event: any) => {
                      editDamageTriggerRef.current =
                        event.currentTarget as HTMLElement;
                      openDamageEditor(data.id);
                    }}
                  />
                ) : null}
                {canRemoveDamageLine ? (
                  <TooltipIcon
                    color="#c92a2a"
                    icon={faTrashCan}
                    title={t('delete')}
                    onClick={() => handleDeleteDamageLine(data.id)}
                  />
                ) : null}
              </>
            )}
          </Space>
        ),
        align: 'center',
        width: 150,
        fixed: 'right',
        exportable: false,
      },
    ],
    [
      canApproveDamage,
      canChangeDamageType,
      canChangePartComponent,
      canChangeRepairMethod,
      canChangeSeverity,
      canChangeWorkCenter,
      canEditDamage,
      canEditDamageDetail,
      canShowPreTimeSuggestions,
      canRemoveDamageLine,
      damageParts,
      dataSource,
      gradePreview,
      canEditWorkCentreInline,
      conditionCodesByPart,
      lookupPending,
      handleDeleteDamageLine,
      handleUnDeleteDamageLine,
      hasGbaAccess,
      hasDamageApproveAccess,
      repairsByKey,
      severitiesByKey,
      showPreTimeDetail,
      detailDamageId,
      canEditRepair,
      canEditSeverity,
      openDamageEditor,
      restoreDamageLine,
      stagedRowIds,
      suggestionById,
      t,
      workCenters,
    ],
  );

  const visibleColumns = columns.filter(
    (column) =>
      column.dataIndex == 'id' ||
      columnFeatures.some(
        (feature) =>
          feature.dataIndex === column.dataIndex &&
          hasFeatureAccess(feature.feature, customerId),
      ),
  ) as CustomColumns<Damage>;

  return (
    <>
      {batchAuthorizationStatus ? (
        <BatchActionsPopup
          visible
          authorizationStatus={batchAuthorizationStatus}
          loading={batchUpdateLoading}
          onSubmit={handleBatchChangeStatus}
          onClose={() => setBatchAuthorizationStatus(undefined)}
        />
      ) : null}

      <ActionsPopup
        visible={!!authorization}
        onSubmit={handleAuthorizationSubmit}
        loading={updateAuthorizationLoading}
        onClose={() => setAuthorization(undefined)}
        authorizationId={authorization?.id}
        authorizationStatus={authorization?.status}
        backToDefault={authorization?.backToDefault}
      />

      <DamageLinePopup
        mode="add"
        open={createDamageLine}
        inspection={inspection}
        onSubmit={reloadData}
        onClose={() => setCreateDamageLine(false)}
      />

      <DamageLinePopup
        mode="edit"
        open={!!editDamageLine}
        damageLine={editDamageLine}
        originalDamageLine={editOriginalDamage}
        inspection={inspection}
        onSubmit={reloadData}
        onStage={handleStageDamage}
        onRestore={restoreDamageLine}
        staged={!!editDamageLine && stagedRowIds.has(editDamageLine.id)}
        onClose={() => setEditDamageLine(undefined)}
        afterClose={() => editDamageTriggerRef.current?.focus()}
      />

      <AddDamageActionsPopup
        inspectionId={inspection.id}
        damageLines={editableDamages}
        preferredDamageLineIds={
          selectedDamage && !isIstaDamageLine(selectedDamage.mechanicalFailure)
            ? [selectedDamage.id]
            : undefined
        }
        isLabour={isLabour}
        visible={addActions}
        canEditFr={canEditFr}
        canEditParts={canEditParts}
        damageLinesLoading={damageLinesLoading}
        onSubmit={reloadData}
        onClose={() => setAddActions(false)}
        afterClose={() => addFrTriggerRef.current?.focus()}
      />

      <AddDamagePartsPopup
        inspectionId={inspection.id}
        damageLines={editableDamages}
        isLabour={isLabour}
        visible={addParts}
        canEditParts={canEditParts}
        damageLinesLoading={damageLinesLoading}
        onSubmit={reloadData}
        onClose={() => setAddParts(false)}
      />

      <DamageLineReviewBar
        view={lineView}
        onViewChange={setLineView}
        total={dataSource.length}
        removed={dataSource.filter(isRemovedDamageLine).length}
        review={dataSource.filter((row) => isPtReviewRequired(row)).length}
        showRemovedBanner={
          !dismissedNotices.removed && dataSource.some(isRemovedDamageLine)
        }
        showChangedBanner={
          !dismissedNotices.changed &&
          inspection.inspectionStatusName ===
            InspectionStatusMap.AwaitingAuthorisation &&
          dataSource.some(isInternallyAmendedDamageLine)
        }
        showPtBanner={
          !dismissedNotices.pt && dataSource.some(isPtReviewRequired)
        }
        onDismiss={(key) =>
          setDismissedNotices((current) => ({ ...current, [key]: true }))
        }
      />

      <UnsavedDamageChangesPrompt when={stagedRowIds.size > 0} />

      {canShowPreTimeSuggestions ? (
        <PreTimeAssistantSummary
          inspectionId={inspection.id}
          excludedDamageLineIds={istaDamageLineIds}
        />
      ) : null}

      <CustomTable
        className="separateRowsTable"
        rowKey="id"
        size="small"
        loading={
          damageLinesLoading ||
          lookupLoading ||
          updateAuthorizationLoading ||
          batchUpdateDamageLinesLoading ||
          batchUpdateLoading ||
          deleteLoading ||
          unDeleteLoading
        }
        dataSource={tableDamageLines}
        columns={visibleColumns}
        headerActions={{
          left: (
            <div
              className="px-3 py-1 border rounded"
              style={{ background: '#f0f3fd' }}
            >
              <DeferredRowClearCapture clearRowRef={clearDeferredRowRef} />
              <PreTimeMetrics validation={preTimeAggregate} />
            </div>
          ),
          right:
            canEditFr || canEditParts || canApproveDamage ? (
              <>
                {canEditFr && editableDamages.length > 0 ? (
                  <Tooltip title={addFrShortcutHint()}>
                    <Button
                      ref={addFrTriggerRef}
                      title={addFrShortcutHint()}
                      onClick={() => {
                        setAddActions(true);
                        requestAnimationFrame(() => {
                          document
                            .querySelector(
                              `[data-row-key="${selectedDamageId}"]`,
                            )
                            ?.scrollIntoView({ block: 'nearest' });
                        });
                      }}
                    >
                      Add {isLabour ? 'labour' : 'flat rates'}
                    </Button>
                  </Tooltip>
                ) : null}
                {canEditParts && editableDamages.length > 0 ? (
                  <Tooltip title={addPartsShortcutHint()}>
                    <Button
                      title={addPartsShortcutHint()}
                      onClick={() => setAddParts(true)}
                    >
                      Add parts
                    </Button>
                  </Tooltip>
                ) : null}

                {canApproveDamage && editableDamages.length > 0 ? (
                  <>
                    <Button
                      color="green"
                      variant="outlined"
                      icon={<Icon icon={faFileCircleCheck} />}
                      onClick={() =>
                        setBatchAuthorizationStatus(
                          AuthorizationStatus.Approved,
                        )
                      }
                    >
                      {t('approveAll')}
                    </Button>
                    <Button
                      color="danger"
                      variant="outlined"
                      icon={<Icon icon={faFileCircleXmark} />}
                      onClick={() =>
                        setBatchAuthorizationStatus(
                          AuthorizationStatus.Rejected,
                        )
                      }
                    >
                      {t('rejectAll')}
                    </Button>
                  </>
                ) : null}
              </>
            ) : undefined,
        }}
        onAdd={canEditDamage ? () => setCreateDamageLine(true) : undefined}
        deferInlineEdit={true}
        applyLabel={t('applyChangesCount', { count: stagedRowIds.size })}
        externalPendingCount={Object.keys(stagedEdits).length}
        onDeferredInlineChange={(_, record, changes) => {
          setInlineChanges((current) => {
            const next = { ...current };
            if (Object.keys(changes).length === 0) delete next[record.id];
            else next[record.id] = changes;
            return next;
          });
          const partCode = String(
            'partCode' in changes
              ? (changes.partCode ?? '')
              : (record.partCode ?? ''),
          );
          const damageTypeCode = String(
            'damageConditionCode' in changes
              ? (changes.damageConditionCode ?? '')
              : (record.damageConditionCode ?? ''),
          );
          const severityRaw =
            'severityId' in changes ? changes.severityId : record.severityId;
          const severityId = !severityRaw ? undefined : Number(severityRaw);
          if (partCode) void loadConditionCodes(partCode);
          if (partCode && damageTypeCode) {
            void loadSeverities(partCode, damageTypeCode);
          }
          if (partCode && damageTypeCode && severityId) {
            void loadRepairs(partCode, damageTypeCode, severityId);
            void loadSeverities(partCode, damageTypeCode).then((list) => {
              const match = list.find((item) => item.severityId === severityId);
              if (!match) return;
              setGradePreview((current) => ({
                ...current,
                [record.id]: {
                  valueForGrading: match.valueForGrading,
                  salesGradeValue: match.salesGradeValue,
                },
              }));
            });
            return;
          }

          setGradePreview((current) => {
            if (!(record.id in current)) return current;
            const next = { ...current };
            delete next[record.id];
            return next;
          });
        }}
        onApplyComplete={handleDeferredApplyComplete}
        rowClassName={(record) =>
          [
            getColorForAuthorizationStatus(record),
            record.id === selectedDamageId ? 'pt-selected-row' : '',
          ]
            .filter(Boolean)
            .join(' ')
        }
        onRow={(record) => ({
          onClick: () => setSelectedDamageId(record.id),
        })}
        scroll={{ x: 1600, y: '50vh' }}
        exportOptions={{ fileName: 'Damage Lines' }}
        pagination={false}
      />
      <DamageLineReviewFooter
        total={dataSource.length}
        removed={dataSource.filter(isRemovedDamageLine).length}
        review={dataSource.filter((row) => isPtReviewRequired(row)).length}
      />

      <PtReviewRequiredModal
        open={!!ptReviewDamage}
        damage={ptReviewDamage}
        submitting={ptReviewSubmitting}
        onCancel={() => {
          if (!ptReviewSubmitting) setPtReviewDamage(undefined);
        }}
        onKeep={() =>
          ptReviewDamage &&
          !ptReviewSubmitting &&
          void submitPreTimingReview(ptReviewDamage, 'keep')
        }
        onRemove={() =>
          ptReviewDamage &&
          !ptReviewSubmitting &&
          void submitPreTimingReview(ptReviewDamage, 'remove')
        }
      />

      {canShowPreTimeSuggestions ? (
        <PreTimeSuggestionDetail
          line={suggestionLine}
          damage={dataSource.find(
            (row) => row.id === suggestionLine?.damageLineId,
          )}
          inspection={inspection}
          canEditFr={canEditFr}
          canEditParts={canEditParts}
          onClose={() => setSuggestionDamageId(undefined)}
          onReload={onReload}
        />
      ) : null}

      <DamageColourLegend />

      <Drawer
        placement="right"
        width="80%"
        open={!!detailDamage}
        closable={false}
        keyboard
        onClose={() => setDetailDamageId(undefined)}
        destroyOnHidden
        className="damage-detail-drawer"
        classNames={{ header: 'p-1', body: 'p-2 overflow-hidden' }}
      >
        {detailDamage ? (
          <DamageDetailPanel
            damage={detailDamage}
            inspectionId={inspection.id}
            isLabour={isLabour}
            canApprove={canApproveDamage}
            canEditFr={canEditFr}
            canEditParts={canEditParts}
            canUpdateImage={canEditDamageDetail(detailDamage, canEditDamage)}
            onApprove={() =>
              setAuthorization({
                id: detailDamage.id,
                status: AuthorizationStatus.Approved,
              })
            }
            onReject={() =>
              setAuthorization({
                id: detailDamage.id,
                status: AuthorizationStatus.Rejected,
              })
            }
            onEdit={() => setEditDamageLine(detailDamage)}
            onRevertAuthorization={() =>
              setAuthorization({
                id: detailDamage.id,
                status: detailDamage.authorizationStatusNum,
                backToDefault: true,
              })
            }
            onUnDelete={() => handleUnDeleteDamageLine(detailDamage.id)}
            onClose={() => setDetailDamageId(undefined)}
            damageLinesLoading={damageLinesLoading}
            refetch={reloadData}
          />
        ) : null}
      </Drawer>
    </>
  );
};
